// @flow strict
import * as React from 'react'
import { type ContentfulNode } from '@mondough/contentful'
import { subtitleNodeName } from '@mondough/contentful/src/richTextDecorators'
import {
  getHeroSectionFieldsFromContentful,
  getTextAndImageSectionFieldsFromContentful,
  getTriptychSectionFieldsFromContentful,
  getEmailSignUpFormFieldsFromContentful,
  getMarkdownSectionFieldsFromContentful,
  getCTASectionFieldsFromContentful,
  getSubtitleSectionFieldsFromContentful,
  getBlogRollSectionFieldsFromContentful,
  getBannerFieldsFromContentful,
  getDisclosureGroupFieldsFromContentful,
} from './extractContentfulFields'
import HeroSection from '../components/HeroSection'
import TextAndImageSection from '../components/TextAndImageSection'
import TriptychSection from '../components/TriptychSection'
import VerticalTriptychSection from '../components/VerticalTriptychSection'
import EmailSignUpForm from '../components/EmailSignUpForm'
import MarkdownSection from '../components/MarkdownSection'
import CTASection from '../components/CTASection'
import SubtitleSection from '../components/SubtitleSection'
import BlogRollSection from '../components/BlogRollSection'
import Banner from '../components/Banner'
import DisclosureGroup from '../components/DisclosureGroup'

// "Default" here refers to the default Monzo theme

export const defaultSubtitleSection = (
  node: ContentfulNode,
  options?: { leftAlign?: boolean },
) => {
  const { subtitle, blurb } = getSubtitleSectionFieldsFromContentful(node)

  return (
    <SubtitleSection
      subtitle={subtitle}
      blurb={blurb}
      leftAlign={options && options.leftAlign === true}
    />
  )
}

export const defaultHero = (node: ContentfulNode) => {
  const {
    title,
    description,
    imageUrls,
    cta,
    flags,
  } = getHeroSectionFieldsFromContentful(node)

  return (
    <HeroSection
      title={title}
      description={description}
      imageUrls={imageUrls}
      cta={cta}
      flags={flags}
    />
  )
}

export const defaultTextAndImage = (node: ContentfulNode) => {
  const {
    title,
    description,
    imageUrls,
    imageAlts,
    reverse,
    cta,
  } = getTextAndImageSectionFieldsFromContentful(node)
  return (
    <TextAndImageSection
      title={title}
      description={description}
      imageUrls={imageUrls}
      imageAlts={imageAlts}
      reverse={reverse}
      cta={cta}
    >
      {node[subtitleNodeName] && defaultSubtitleSection(node[subtitleNodeName])}
    </TextAndImageSection>
  )
}

export const defaultTriptych = (node: ContentfulNode) => {
  const {
    sections,
    reverse,
    flags,
    direction,
  } = getTriptychSectionFieldsFromContentful(node)

  if (direction === 'vertical') {
    return (
      <VerticalTriptychSection
        sections={sections}
        flags={flags}
        reverse={reverse}
      >
        {node[subtitleNodeName] &&
          defaultSubtitleSection(node[subtitleNodeName])}
      </VerticalTriptychSection>
    )
  }

  return (
    <TriptychSection sections={sections} reverse={reverse} flags={flags}>
      {node[subtitleNodeName] && defaultSubtitleSection(node[subtitleNodeName])}
    </TriptychSection>
  )
}

export const defaultEmailSignUpForm = (node: ContentfulNode) => {
  const {
    buttonText,
    successMessage,
    titleText,
    waitlistId,
    userConsentText,
    clientId,
    clientSecret,
  } = getEmailSignUpFormFieldsFromContentful(node)

  return (
    <EmailSignUpForm
      buttonText={buttonText}
      clientId={clientId}
      clientSecret={clientSecret}
      successMessage={successMessage}
      titleText={titleText}
      userConsentText={userConsentText}
      waitlistId={waitlistId}
    />
  )
}

export const defaultMarkdownSection = (node: ContentfulNode) => {
  const { markdownContent, reverse } = getMarkdownSectionFieldsFromContentful(
    node,
  )
  return (
    <MarkdownSection markdownContent={markdownContent} reverse={reverse}>
      {node[subtitleNodeName] && defaultSubtitleSection(node[subtitleNodeName])}
    </MarkdownSection>
  )
}

export const defaultCTASection = (node: ContentfulNode) => {
  const {
    ctaType,
    title,
    subtitle,
    reverse,
  } = getCTASectionFieldsFromContentful(node)
  return (
    <CTASection
      ctaType={ctaType}
      title={title}
      subtitle={subtitle}
      reverse={reverse}
    />
  )
}

export const defaultBlogRoll = (node: ContentfulNode) => {
  const {
    numRows,
    categoryName,
    reverse,
  } = getBlogRollSectionFieldsFromContentful(node)
  return (
    <div>
      <BlogRollSection
        numRows={numRows}
        categoryName={categoryName}
        reverse={reverse}
      >
        {node[subtitleNodeName] &&
          defaultSubtitleSection(node[subtitleNodeName])}
      </BlogRollSection>
    </div>
  )
}

/**
 * Note that the Banner is not a section, but rather a stand-alone component and
 * so cannot inherit sibling titles nor is wrapped by a SectionWrapper like
 * other sections.
 */
export const defaultBanner = (node: ContentfulNode) => {
  const { title, link, icon } = getBannerFieldsFromContentful(node)
  return <Banner title={title} link={link} icon={icon} />
}

export const defaultDiscloureGroup = (node: ContentfulNode) => {
  const { items, flags } = getDisclosureGroupFieldsFromContentful(node)
  return (
    <DisclosureGroup items={items} flags={flags}>
      {node[subtitleNodeName] && defaultSubtitleSection(node[subtitleNodeName])}
    </DisclosureGroup>
  )
}
